import { useCallback, useEffect, useRef, useState } from "react";
import { webinarService } from "../services/webinarService";
import { ListSessionsQuery, WebinarServiceError } from "../types/dto";
import {
  SessionStatus,
  SessionStatusCounts,
  WebinarSession,
  WebinarTopic,
} from "../types/models";

type LoadState = {
  loading: boolean;
  refreshing: boolean;
  error: string | null;
};

const emptyCounts = (): SessionStatusCounts => ({
  all: 0,
  draft: 0,
  scheduled: 0,
  live: 0,
  ended: 0,
  cancelled: 0,
});

export type WebinarListQuery = {
  status: "all" | SessionStatus;
  q: string;
  page: number;
  limit: number;
};

export function useWebinarData(query: WebinarListQuery) {
  const [sessions, setSessions] = useState<WebinarSession[]>([]);
  const [topics, setTopics] = useState<WebinarTopic[]>([]);
  /** Tab badges — always from API `counts` (including search). */
  const [counts, setCounts] = useState<SessionStatusCounts>(emptyCounts);
  const [total, setTotal] = useState(0);
  const [state, setState] = useState<LoadState>({
    loading: true,
    refreshing: false,
    error: null,
  });
  const hasLoadedRef = useRef(false);

  const refresh = useCallback(async () => {
    const isFirst = !hasLoadedRef.current;
    setState({
      loading: isFirst,
      refreshing: !isFirst,
      error: null,
    });
    try {
      const apiQuery: ListSessionsQuery = {
        page: query.page,
        limit: query.limit,
      };
      if (query.status !== "all") {
        apiQuery.status = query.status;
      }
      if (query.q.trim()) {
        apiQuery.q = query.q.trim();
      }
      const page = await webinarService.listSessionsPage(apiQuery);
      setSessions(page.sessions);
      setTopics(page.topics);
      setTotal(page.pagination.total);
      setCounts(page.counts);

      hasLoadedRef.current = true;
      setState({ loading: false, refreshing: false, error: null });
    } catch (e) {
      const message =
        e instanceof WebinarServiceError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Failed to load webinars";
      setState({ loading: false, refreshing: false, error: message });
    }
  }, [query.status, query.q, query.page, query.limit]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    sessions,
    topics,
    counts,
    total,
    page: query.page,
    limit: query.limit,
    loading: state.loading,
    refreshing: state.refreshing,
    error: state.error,
    refresh,
    setTopics,
    setSessions,
  };
}
